
import { Component, Emit, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import type { VinculoPontoUnidade } from '../types-registro-ponto';
import RegistroPontoNovo from '../RegistroPontoNovo.vue';
import CancelError from '../CancelError';
import _ from 'lodash';
import SnackbarUtils from '@tekvue/assets/utils/SnackbarUtils';
import Util from '@/assets/ts/utils/Util';
import ProgressBar from '@/components/ProgressBar.vue';

@Component({
  components: {
    ProgressBar,
  },
})
export default class RegistroPontoQrCode extends Vue {
  //#region [ CONSTANTS ]
  readonly SCREEN_TIMEOUT = 30_000 as const; // 30 seconds
  //#endregion



  //#region [ DATA ]
  html5QrCode: Html5Qrcode | null = null;
  screenTimeout: number | null = null;
  scannerTimeout: number | null = null;
  @Inject() findVinculo!: typeof RegistroPontoNovo.prototype.findVinculo;
  promise: {
    resolve: (value: VinculoPontoUnidade | PromiseLike<VinculoPontoUnidade>) => void;
    reject: (reason?: any) => void;
  } | null = null;
  //#endregion



  //#region [ COMPUTED ]
  get progressBar(): ProgressBar {
    return this.$refs.progressBar as ProgressBar || null;
  }
  //#endregion



  //#region [ EVENTS ]
  @Emit('cancel')
  emitCancel(): void {
    if (this.promise) {
      this.promise.reject(new CancelError());
      this.promise = null; // Reset after rejecting
    }

    this.end();
  }
  //#endregion



  //#region [ LIFECYCLE ]
  beforeDestroy() {
    this.end();
  }
  //#endregion



  //#region [ METHODS ]
  async startScanner() {
    const qrRegionId = 'qr-reader';
    this.html5QrCode = new Html5Qrcode(qrRegionId);

    try {
      await this.html5QrCode.start(
        { facingMode: 'user' },
        {
          fps: 10,
        },
        (decodedText: string) => {
          this.onQrCodeScanned(decodedText);
        },
        (error: any) => {
          // Ignore scan errors
        }
      );
    } catch (err) {
      if (typeof err === 'string' && err.includes('NotReadableError')) {
        throw new Error('Não foi possível acessar a câmera.');
      } else {
        throw err;
      }
    }
  }

  stopScanner() {
    if (this.html5QrCode && this.html5QrCode.getState() != Html5QrcodeScannerState.NOT_STARTED) {
      this.html5QrCode.stop().then(() => {
        this.html5QrCode.clear();
      });
    }

    this.html5QrCode = null;
  }

  pauseScannerTemporarily(duration: number) {
    if (this.html5QrCode) {
      this.html5QrCode.pause();

      if (this.scannerTimeout) {
        window.clearTimeout(this.scannerTimeout);
      }

      this.scannerTimeout = window.setTimeout(() => {
        this.resumeScanner();
      }, duration); // Resume after 5 seconds
    }
  }

  resumeScanner() {
    if (this.html5QrCode && this.scannerTimeout) {
      window.clearTimeout(this.scannerTimeout);
      this.scannerTimeout = null;
      this.html5QrCode.resume();
    }
  }

  onQrCodeScanned(qrCode: string) {
    if (!this.promise) {
      return;
    }

    if (!Util.isNumeric(qrCode)) {
      this.pauseScannerTemporarily(3000);
      SnackbarUtils.showMessage(
        `QR Code inválido. (${qrCode})`,
        SnackbarUtils.TYPE_ALERT,
        3000
      );

      return;
    }

    const vinculo = this.findVinculo(qrCode);

    if (!vinculo) {
      this.pauseScannerTemporarily(3000);
      SnackbarUtils.showMessage(
        `Vínculo não identificado. (${qrCode})`,
        SnackbarUtils.TYPE_ALERT,
        3000
      );

      return;
    }

    if (this.promise) {
      this.stopScanner();
      this.clearScreenTimeout();
      this.promise.resolve(vinculo);
      this.promise = null;
    }
  }

  scan(): Promise<VinculoPontoUnidade> {
    return new Promise(async (resolve, reject) => {
      this.promise = {
        resolve,
        reject,
      };

      try {
        await this.startScanner();
        this.progressBar.start(this.SCREEN_TIMEOUT);

        // Set a timeout to auto-cancel after 30 seconds if not resolved
        this.screenTimeout = window.setTimeout(() => {
          this.emitCancel();
        }, this.SCREEN_TIMEOUT);
      } catch (err) {
        reject(err);
      }
    });
  }

  clearScreenTimeout() {
    if (this.screenTimeout) {
      window.clearTimeout(this.screenTimeout);
      this.screenTimeout = null;
    }
  }

  clearScannerTimeout() {
    if (this.scannerTimeout) {
      window.clearTimeout(this.scannerTimeout);
      this.scannerTimeout = null;
    }
  }

  end(): void {
    this.stopScanner();
    this.clearScreenTimeout();
    this.clearScannerTimeout();
  }
  //#endregion
}
